import { injectable, injectFromBase } from 'inversify'
import AbstractPaginationServiceFactory from '@/services/tables/pagination/abstractPaginationServiceFactory'
import {
  type TokenPaginationUrlParams,
  firstPageNumber,
  lastTokenForFirstPage,
} from '@/services/tables/pagination/types'
import type { LowLevelUpdateBody } from '@/services/vuex/types'
import type Pagination from '@/data/models/tables/Pagination'

@injectable()
@injectFromBase()
export default class TokenPaginationServiceFactory extends AbstractPaginationServiceFactory<TokenPaginationUrlParams> {
  /**
   * Cache lastId (list of tokens) for each perPage value.
   * Fix repair lastId for cached pages after perPage change.
   *
   * key – perPage (number of elements on the page)
   * value – a string containing a list of tokens, glued with the "-" symbol
   */
  protected readonly tokensCache: Record<number, string> = {}

  public init(params?: TokenPaginationUrlParams) {
    const defaultModelData = this.getDefaultModelData()
    const modelData = this.buildModelData({
      ...defaultModelData,
      ...params,
      perPage: params?.perPage || defaultModelData.perPage,
    })

    this.repo.insertOrUpdateRaw([modelData])

    return true // The paging record is missing and should re-init the pagination
  }

  public isFirstPage(): boolean {
    const { page } = this.getData()
    return page === 1
  }

  public moveToPrevPage() {
    const data = this.getData()
    const page = data.page - 1
    const tokensList = this.getTokensList()
    const nextId = tokensList[page]
    const pageKey = this.pageKey({ page, perPage: data.perPage })

    const requestParams = {
      lastId: data.lastId,
      nextId,
      perPage: data.perPage,
      page,
      currentPage: page,
      pageKey,
    }

    const modelData = {
      id: this.tableModelId,
      ...requestParams,
      totalCount: data.totalCount,
      pageCount: data.pageCount,
      page,
      currentPage: page,
      pageKey,
    }
    this.setData(modelData)

    return { requestParams, pageKey }
  }

  public moveToFirstPage() {
    return this.moveToPage(this.getFirstPage())
  }

  public moveToNextPage(nextPageData) {
    const data = this.getData()
    const page = data.page + 1
    const tokensList = this.addToken(nextPageData.lastId)
    const nextId = tokensList[data.page]
    const pageKey = this.pageKey({ page, perPage: data.perPage })

    const requestParams = {
      lastId: tokensList.join('-'),
      nextId,
      perPage: data.perPage,
      page,
      currentPage: page,
      pageKey,
    }

    return this.moveToPage({ requestParams, pageKey })
  }

  public pageRequestParams() {
    const { page, nextId, perPage } = this.getData()
    return this.buildPageParams(page, nextId, perPage)
  }

  public getFirstPage() {
    const {
      requestParams: { perPage },
    } = this.pageRequestParams()
    return this.buildPageParams(1, lastTokenForFirstPage, perPage)
  }

  public toQuery(): TokenPaginationUrlParams {
    const { nextId, perPage } = this.getData() || this.getDefaultModelData()
    return { nextId, perPage }
  }

  public pageKey(data) {
    const { page, perPage } = data
    return `${page}-${perPage}`
  }

  // It doesn't call when perPage changed. Using getFirstPage instead.
  public changePerPage(perPage: number) {
    const paginationParams = {
      currentPage: firstPageNumber,
      lastId: lastTokenForFirstPage,
      perPage,
    }
    const modelData = this.buildModelData(paginationParams)
    this.setData(modelData)
  }

  public setData(pagination: LowLevelUpdateBody<Pagination>) {
    const prevData = this.getData()
    if (prevData) {
      this.setCachedLastId(prevData.perPage, prevData.lastId)
    }
    const data = this.getData()
    const lastId = this.getNewLastId(pagination)

    const isPerPageChanged = pagination.perPage && data.perPage !== pagination.perPage

    const newData = {
      ...data,
      perPage: pagination.perPage,
      pageKey: pagination.pageKey,
      nextId: isPerPageChanged ? lastTokenForFirstPage : pagination.nextId,
      lastId,
      totalCount: pagination.totalCount,
      page: pagination.page ?? data.page,
      currentPage: pagination.currentPage ?? data.currentPage,
    }
    super.setData(newData)

    if (newData.perPage) {
      this.setCachedLastId(newData.perPage, newData.lastId)
    }
  }

  protected getCachedLastId(perPage: number) {
    return this.tokensCache[perPage] ?? ''
  }

  protected setCachedLastId(perPage: number, lastId: string) {
    this.tokensCache[perPage] = lastId
  }

  // Use this method for changePerPage logic
  protected getNewLastId(pagination: LowLevelUpdateBody<Pagination>) {
    const data = this.getData()
    if (pagination.perPage && data.perPage !== pagination.perPage) {
      return this.getCachedLastId(pagination.perPage)
    }
    const tokensList = this.addToken(pagination.nextId || '')
    return tokensList.join('-')
  }

  protected buildPageParams(page: number, nextId: string, perPage: number) {
    const requestParams = { nextId, perPage }
    const pageKey = this.pageKey({ page, perPage })
    return { requestParams, pageKey, currentPage: page, page }
  }

  public applyUrlParams(params: TokenPaginationUrlParams) {
    const data = this.getData()

    const isPerPageChanged = params.perPage && data.perPage !== params.perPage
    const page = isPerPageChanged ? 1 : data.page
    const pageKey = this.pageKey({ page, perPage: params.perPage })

    const modelData = {
      id: this.tableModelId,
      ...params,
      pageKey,
      totalCount: data.totalCount,
      pageCount: data.pageCount,
      page,
      currentPage: page,
    }
    this.setData(modelData)
  }

  public isDefaultState() {
    return false
  }

  public onDataLoad(): void {}

  public getTotal(): number {
    return 0
  }

  protected buildModelData(paginationParams) {
    const data = this.getDataOrNull()

    /*
     * The order of expansion is important:
     *   1. First, take the current value of `lastId` – so that it is the default
     *   2. Then apply the passed parameters – if there is a `lastId`
     *     in them, it will overwrite the current value. This allows
     *     you to correctly save the formed list of tokens when moving
     *     to the next page.
     */
    return {
      id: this.tableModelId,
      lastId: data?.lastId ?? '',
      ...paginationParams,
    }
  }

  protected getDefaultModelData() {
    const rawModelData = {
      lastId: lastTokenForFirstPage,
      nextId: lastTokenForFirstPage,
      page: 1,
      currentPage: 1,
      perPage: this.settings.perPage,
      totalCount: 0,
    }
    return this.buildModelData(rawModelData)
  }

  protected getTokensList() {
    const data = this.getData()
    return data.lastId.split('-')
  }

  protected addToken(newToken: string) {
    const tokensList = this.getTokensList()
    if (!tokensList.includes(newToken) && newToken !== '0') {
      tokensList.push(newToken)
    }
    return tokensList
  }

  public setTotalCount(_count: number): void {}
}
